/**
 * AkteFileThumbnail — kleine Vorschau einer Datei (Erste-PDF-Seite oder Bild).
 *
 * Lazy: rendert erst wenn die Row im Viewport ist (IntersectionObserver), damit
 * eine Akte mit 100 Belegen nicht 100 PDFs gleichzeitig in den Worker schickt.
 * Fuer Nicht-PDF/-Image: kleines Typ-Badge (PDF/DOC/ZIP) — kein Blob-Laden.
 */
import { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './pdfWorker';
import type { PropertyFileResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { useFileBlob } from './useFileBlob';

interface Props {
  propertyId: string;
  file: PropertyFileResponse;
  width?: number;
}

export function AkteFileThumbnail({ propertyId, file, width = 56 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);

  const isPdf = file.contentType === 'application/pdf';
  const isImage = file.contentType.startsWith('image/');
  const height = Math.round(width * 1.35);

  return (
    <div
      ref={containerRef}
      className={cn(
        'bg-card border-line shrink-0 overflow-hidden rounded border',
        'flex items-center justify-center',
      )}
      style={{ width, height }}
    >
      {!visible && <Skeleton />}
      {visible && isPdf && <PdfThumb propertyId={propertyId} file={file} width={width} />}
      {visible && isImage && <ImageThumb propertyId={propertyId} file={file} />}
      {visible && !isPdf && !isImage && <TypeBadge fileName={file.fileName} />}
    </div>
  );
}

function PdfThumb({
  propertyId,
  file,
  width,
}: {
  propertyId: string;
  file: PropertyFileResponse;
  width: number;
}) {
  const { url, error } = useFileBlob(propertyId, file.id);
  if (error) return <TypeBadge fileName={file.fileName} />;
  if (!url) return <Skeleton />;
  return (
    <Document file={url} loading={<Skeleton />} error={<TypeBadge fileName={file.fileName} />}>
      <Page pageNumber={1} width={width} renderAnnotationLayer={false} renderTextLayer={false} />
    </Document>
  );
}

function ImageThumb({ propertyId, file }: { propertyId: string; file: PropertyFileResponse }) {
  const { url, error } = useFileBlob(propertyId, file.id);
  if (error) return <TypeBadge fileName={file.fileName} />;
  if (!url) return <Skeleton />;
  return <img src={url} alt={file.fileName} className="h-full w-full object-cover" />;
}

function Skeleton() {
  return <div className="bg-card-alt h-full w-full animate-pulse" />;
}

function TypeBadge({ fileName }: { fileName: string }) {
  const ext = fileName.split('.').pop()?.toUpperCase() ?? 'FILE';
  return <span className="text-ink-faint font-mono text-[9px] uppercase tabular-nums">{ext}</span>;
}
